/**
 * PhaseInspectionsDialog — v2.5.6
 *
 * Per-phase inspection manager. Used for HVAC / Electrical / Plumbing /
 * Concrete / Framing line items where one or more passing inspection reports
 * must be on file before a payment can be recorded.
 *
 * Each inspection slot accepts a PDF or photo of the passing report. Owners
 * can also override a slot to "passed" with a written reason (audit trail
 * preserved). Deleting a slot removes the uploaded file from GridFS.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Plus, Upload, Trash2, ShieldCheck, ShieldAlert, FileText, Loader2, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../lib/api';
import { formatDate } from '../lib/dateUtils';

const SUGGESTED_TYPES = [
  { value: 'rough', label: 'Rough-in' },
  { value: 'final', label: 'Final' },
  { value: 'slab', label: 'Slab' },
  { value: 'footing', label: 'Footing' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'other', label: 'Other' },
];

export default function PhaseInspectionsDialog({ open, onOpenChange, phase, onGateChange, onViewFile }) {
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [gate, setGate] = useState(null);

  // Add-form state
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('rough');
  const [adding, setAdding] = useState(false);

  // Override modal state
  const [overrideForId, setOverrideForId] = useState(null);
  const [overrideNote, setOverrideNote] = useState('');
  const [overriding, setOverriding] = useState(false);

  // Per-row uploading state
  const [uploadingId, setUploadingId] = useState(null);

  const refresh = useCallback(async () => {
    if (!phase?.id) return;
    setLoading(true);
    try {
      const res = await apiService.listPhaseInspections(phase.id);
      setInspections(res?.data?.inspections || []);
      setGate(res?.data?.gate || null);
      onGateChangeRef.current?.(res?.data?.gate || null);
    } catch (err) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, [phase?.id]);

  // Stabilize parent callback so refresh isn't recreated on every parent render
  // (which would trigger an infinite refetch via the open/refresh useEffect).
  const onGateChangeRef = useRef(onGateChange);
  useEffect(() => {
    onGateChangeRef.current = onGateChange;
  }, [onGateChange]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error('Give the inspection a name (e.g. "Rough HVAC")');
      return;
    }
    setAdding(true);
    try {
      await apiService.addPhaseInspection(phase.id, {
        name: newName.trim(),
        type: newType,
      });
      setNewName('');
      setNewType('rough');
      await refresh();
      toast.success('Inspection added');
    } catch (err) {
      toast.error('Failed to add inspection');
    } finally {
      setAdding(false);
    }
  };

  const handleUpload = async (inspId, file) => {
    if (!file) return;
    setUploadingId(inspId);
    try {
      const res = await apiService.uploadPhaseInspectionFile(phase.id, inspId, file);
      await refresh();
      const r = res?.data?.ai_result;
      if (r === 'passed') {
        toast.success('AI reviewed the report — inspection PASSED ✓');
      } else if (r === 'failed') {
        toast.error('AI reviewed the report — it shows a FAILED inspection. Not marked passed.');
      } else {
        toast.warning('Report uploaded, but AI could not confirm a pass. Please review and override if it passed.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDelete = async (inspId) => {
    if (!window.confirm('Remove this inspection slot? The uploaded file (if any) will be deleted.')) return;
    try {
      await apiService.deletePhaseInspection(phase.id, inspId);
      await refresh();
      toast.success('Inspection removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleOverride = async () => {
    const note = overrideNote.trim();
    if (note.length < 5) {
      toast.error('Override reason must be at least 5 characters.');
      return;
    }
    setOverriding(true);
    try {
      await apiService.overridePhaseInspection(phase.id, overrideForId, note);
      setOverrideForId(null);
      setOverrideNote('');
      await refresh();
      toast.success('Inspection marked passed via override');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Override failed';
      toast.error(typeof msg === 'string' ? msg : 'Override failed');
    } finally {
      setOverriding(false);
    }
  };

  if (!phase) return null;
  const satisfied = !!gate?.satisfied;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="phase-inspections-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {satisfied ? (
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              ) : (
                <ShieldAlert className="w-5 h-5 text-amber-600" />
              )}
              Inspections · {phase.name}
            </DialogTitle>
            <DialogDescription>
              {satisfied
                ? 'At least one passing inspection report is on file for this phase.'
                : 'Inspection reports for this phase are typically uploaded by the contractor through their compliance portal — they\'re emailed a request as part of the payment flow. You can also attach the report here if you happen to have it on hand.'}
            </DialogDescription>
          </DialogHeader>

          {/* Status banner */}
          <div
            className={`rounded-md border px-3 py-2 text-sm flex items-center justify-between ${
              satisfied
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
            data-testid="inspection-gate-status"
          >
            <span>
              {gate
                ? `${gate.passed_count} of ${Math.max(gate.total_count, gate.passed_count)} inspection${gate.total_count === 1 ? '' : 's'} received`
                : 'Loading…'}
            </span>
            <Badge variant="outline" className={satisfied ? 'border-emerald-400 text-emerald-700' : 'border-amber-400 text-amber-700'}>
              {satisfied ? 'Report received' : 'Awaiting contractor'}
            </Badge>
          </div>

          {/* Inspection list */}
          <div className="space-y-2 mt-3" data-testid="phase-inspections-list">
            {loading && (
              <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading inspections…
              </div>
            )}
            {!loading && inspections.length === 0 && (
              <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                No inspections yet. Add the first one below.
              </div>
            )}
            {inspections.map((insp) => {
              const passed = insp.status === 'passed';
              const overridden = insp.override === true;
              return (
                <div
                  key={insp.id}
                  className={`flex items-start justify-between gap-3 rounded-md border p-3 ${
                    passed ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`inspection-row-${insp.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-800">{insp.name}</span>
                      {insp.type && (
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
                          {insp.type}
                        </Badge>
                      )}
                      {passed && !overridden && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]">
                          ✓ Passed
                        </Badge>
                      )}
                      {overridden && (
                        <Badge className="bg-amber-600 hover:bg-amber-600 text-white text-[10px]">
                          ⚠ Override
                        </Badge>
                      )}
                      {!passed && (
                        <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                          Pending
                        </Badge>
                      )}
                    </div>
                    {insp.filename && (
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> {insp.filename}
                        {insp.uploaded_at && (
                          <span className="ml-1">· uploaded {formatDate(insp.uploaded_at)}</span>
                        )}
                      </div>
                    )}
                    {insp.file_id && onViewFile && (
                      <button
                        type="button"
                        onClick={() => onViewFile(insp.file_id, insp.filename)}
                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900 hover:underline"
                        data-testid={`inspection-view-file-${insp.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> View / Download report
                      </button>
                    )}
                    {overridden && insp.override_note && (
                      <div className="text-xs text-amber-900 mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        <strong>Override reason:</strong> {insp.override_note}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!passed && (
                      <>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp,image/heic"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleUpload(insp.id, f);
                              e.target.value = '';
                            }}
                            data-testid={`inspection-upload-input-${insp.id}`}
                          />
                          <span
                            className="inline-flex items-center h-7 px-2 text-xs rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`inspection-upload-btn-${insp.id}`}
                          >
                            {uploadingId === insp.id ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-3 h-3 mr-1" />
                            )}
                            Owner upload
                          </span>
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                          onClick={() => {
                            setOverrideForId(insp.id);
                            setOverrideNote('');
                          }}
                          data-testid={`inspection-override-btn-${insp.id}`}
                        >
                          Mark received (note)
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                      onClick={() => handleDelete(insp.id)}
                      data-testid={`inspection-delete-btn-${insp.id}`}
                      title="Delete inspection slot"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add new inspection */}
          <div className="mt-4 border-t pt-4">
            <Label className="text-xs font-semibold text-slate-700">Add an inspection</Label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder='e.g. "Rough HVAC", "Final Plumbing"'
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="h-9 text-sm flex-1"
                data-testid="add-inspection-name-input"
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="h-9 text-sm border rounded px-2 bg-white"
                data-testid="add-inspection-type-select"
              >
                {SUGGESTED_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <Button
                onClick={handleAdd}
                disabled={adding}
                className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="add-inspection-submit-btn"
              >
                {adding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                Add
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Trades like HVAC, Electrical, Plumbing, Concrete and Framing typically need 1–3 inspections (rough, slab, final).
            </p>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="phase-inspections-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override note modal */}
      <Dialog open={!!overrideForId} onOpenChange={(o) => !o && setOverrideForId(null)}>
        <DialogContent className="max-w-md" data-testid="inspection-override-dialog">
          <DialogHeader>
            <DialogTitle>Mark inspection received without a file</DialogTitle>
            <DialogDescription>
              Use this when the inspection happened but the report isn't digitized yet (verbal pass, mailed paperwork, etc.). The note is logged on the phase for the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Note (required, ≥ 5 chars)</Label>
            <Textarea
              value={overrideNote}
              onChange={(e) => setOverrideNote(e.target.value)}
              rows={3}
              placeholder="e.g. Inspector verbally confirmed pass — report being mailed."
              data-testid="inspection-override-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideForId(null)}>Cancel</Button>
            <Button
              onClick={handleOverride}
              disabled={overriding || overrideNote.trim().length < 5}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="inspection-override-submit-btn"
            >
              {overriding ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
